import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ChevronRight, ExternalLink, Plus } from "lucide-react";
import { listBusinesses } from "../../../api/chatbotApi";
import { getSchedulingErrorMessage } from "../../../api/schedulingApi";
import useAuth from "../../../hooks/useAuth";
import { isPlatformAdmin, membershipFor } from "../../../util/accessControl";
import { canAccessAgentConfig, defaultBotSection } from "../../../util/routeAccess";
import ClientSwitcher from "../../../components/ClientSwitcher";
import BotCard, { filterBots } from "../../../components/admin/BotCard";

const secondaryAction = "inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]";

export default function BotHomePage() {
  const { user, can } = useAuth();
  const [bots, setBots] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const platform = isPlatformAdmin(user);
  const clientName = membershipFor(user)?.client_name;

  useEffect(() => {
    let active = true;
    setError("");
    setBots(null);
    listBusinesses(user?.client_id).then((rows) => {
      if (!Array.isArray(rows)) throw new Error("Invalid bot list response.");
      if (active) setBots(rows.filter((bot) => bot.id && !bot.deleted_at));
    }).catch((err) => {
      if (active) setError(getSchedulingErrorMessage(err, "Unable to load your bots."));
    });
    return () => { active = false; };
  }, [attempt, user?.client_id]);

  const visibleBots = bots ? filterBots(bots, searchTerm) : [];
  const actionsFor = (bot) => {
    const clientId = bot.client_id ?? user?.client_id;
    const allowed = (permission) => can?.(permission, clientId) === true;
    const id = encodeURIComponent(bot.id);
    return [
      allowed("customers.read") && <Link key="customers" to={`/agents/${id}/customers`}
        className="rounded-lg bg-cyan-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-900">Workspace</Link>,
      platform && <Link key="meta" to={`/agents/${bot.id}/meta-config`} state={{ from: "/agents", fromLabel: "Agents" }}
        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50/60 px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100">
        Meta Config <ExternalLink size={12} /></Link>,
      canAccessAgentConfig(user, clientId) && <Link key="config" to={`/agents/${id}/agent-config`} className={secondaryAction}>
        Agent Config <ChevronRight size={14} /></Link>,
    ].filter(Boolean);
  };
  const titlePath = (bot) => {
    const section = defaultBotSection(user, bot.client_id ?? user?.client_id);
    return `/agents/${encodeURIComponent(bot.id)}${section ? `/${section}` : ""}`;
  };

  return <div className="space-y-6">
    <div className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
      <div aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-xl font-bold text-[#096dea]">
        {clientName ? clientName.charAt(0).toUpperCase() : "A"}
      </div>
      <div className="min-w-0">
        <h1 className="flex flex-wrap items-center gap-2 text-2xl font-bold text-slate-950">
          <ClientSwitcher />
          <span>Agents</span>
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">Open an agent workspace to access the areas allowed by your role.</p>
      </div>
    </div>

    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Briefcase size={20} className="text-[#096dea]" /> AI Bots {bots ? visibleBots.length : ""}
        </h2>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <input type="search" aria-label="Search bots" placeholder="Search bots..." value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm shadow-sm focus:border-[#096dea] focus:outline-none sm:w-64" />
          {platform && user?.client_id != null && <Link to={`/clients/${encodeURIComponent(user.client_id)}/new-bot`}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0959bb]">
            <Plus size={17} /> Add Bot
          </Link>}
        </div>
      </div>

      {error ? <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <p>{error}</p>
        <button type="button" className="mt-2 font-semibold underline" onClick={() => setAttempt((value) => value + 1)}>Retry loading bots</button>
      </div>
        : bots === null ? <p role="status" className="text-sm text-slate-500">Loading bots...</p>
          : bots.length === 0 ? <p className="rounded-2xl border border-slate-200/80 bg-white p-8 text-center text-sm text-slate-500">No bots are available for your account.</p>
            : visibleBots.length === 0 ? <p className="rounded-2xl border border-slate-200/80 bg-white p-8 text-center text-sm text-slate-500">No matching bots.</p>
              : <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">{visibleBots.map((bot) => (
                <BotCard key={bot.id} bot={bot} titleTo={titlePath(bot)}
                  editTo={platform ? `/agents/${encodeURIComponent(bot.id)}/edit` : undefined}>
                  <nav aria-label={`${bot.name || bot.id} workspace`} className="flex flex-wrap items-center justify-end gap-2">
                    {actionsFor(bot)}
                  </nav>
                </BotCard>
              ))}</div>}
    </section>
  </div>;
}
